import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useHints } from "./HintProvider";

/**
 * The gold "Read more ?" affordance (Scope A4). The tag IS the collapsed state;
 * clicking it (or first use of the control it explains, via autoShow) expands a
 * popover. Only one hint is open at a time (enforced by HintProvider); each is
 * individually closable. Keeps the UI clean by default.
 */
export function HintTag({
  id,
  title,
  children,
  side = "bottom",
  align = "right",
}: {
  id: string;
  title?: string;
  children: ReactNode;
  side?: "bottom" | "top";
  align?: "left" | "right";
}) {
  const { openId, toggle, close } = useHints();
  const open = openId === id;
  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={() => toggle(id)}
        aria-expanded={open}
        className="inline-flex items-center gap-0.5 rounded-full border border-[var(--color-border-gold)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-light)]"
      >
        Read more ?
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute z-50 w-64 rounded-lg border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] p-3 text-left shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] ${align === "right" ? "right-0" : "left-0"} ${side === "top" ? "bottom-full mb-2" : "top-full mt-2"}`}
        >
          {title && <span className="mb-1 block font-heading text-[13px] font-medium text-[var(--color-text-primary)]">{title}</span>}
          <span className="block text-[12px] leading-relaxed text-[var(--color-text-muted)]">{children}</span>
          <button
            type="button"
            onClick={close}
            aria-label="Close hint"
            className="absolute right-1.5 top-1.5 text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
    </span>
  );
}
